import { useEffect, useState } from 'react';
import { Grid, InputLabel, MenuItem, OutlinedInput, Select } from '@mui/material';
import InputMask from 'react-input-mask';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import { FormDrawer, FormDrawerGroup } from '../../../../../components/form-drawer';
import { DateField, FieldError } from '../../../../../components/form-page-parts';
import BrazilianNumberInput from '../../../../../components/brazilian-number-input';
import { LEGAL_INSTRUMENT_TYPES } from '../../../../../model/sge/transfer/transfer-enums';
import { ITransferAddEditRequest } from '../../../../../model/sge/transfer/request/transfer-add-edit-request';
import { ITransferResponse } from '../../../../../model/sge/transfer/response/transfer-response';
import { parseNumberInput } from '../../../../../utils/format';
import { hasErrors } from '../../../../../utils/validation';
import { TransferForm, toTransferForm, validateTransfer } from './amendment-detail-form-utils';

interface TransferDrawerProps {
    open: boolean;
    initial?: ITransferResponse;
    isSaving: boolean;
    onClose: () => void;
    onSave: (value: ITransferAddEditRequest) => void;
}

export function TransferDrawer({ open, initial, isSaving, onClose, onSave }: TransferDrawerProps) {
    const [form, setForm] = useState<TransferForm>({});
    const [showValidationErrors, setShowValidationErrors] = useState(false);
    const isEdit = !!initial;
    const validation = validateTransfer(form);
    const errors = showValidationErrors ? validation : {};

    useEffect(() => {
        if (!open) return;
        setForm(toTransferForm(initial));
        setShowValidationErrors(false);
    }, [open, initial]);

    function update(patch: Partial<TransferForm>) {
        setForm((current) => ({ ...current, ...patch }));
    }

    function handleSave() {
        if (hasErrors(validation)) {
            setShowValidationErrors(true);
            return;
        }
        onSave(form as ITransferAddEditRequest);
    }

    return (
        <FormDrawer
            open={open}
            icon={<SwapHorizIcon />}
            title={isEdit ? 'Editar transferência' : 'Nova transferência'}
            subtitle={initial?.companyName}
            saveLabel={isEdit ? 'Salvar alterações' : 'Adicionar transferência'}
            isSaving={isSaving}
            onClose={onClose}
            onSave={handleSave}
        >
            <FormDrawerGroup title="Entidade convenente">
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <InputLabel required>Razão social</InputLabel>
                        <OutlinedInput fullWidth autoFocus autoComplete="off" value={form.companyName || ''} onChange={(e) => update({ companyName: e.target.value })} error={!!errors.companyName} />
                        <FieldError message={errors.companyName} />
                    </Grid>
                    <Grid item xs={12} sm={7}>
                        <InputLabel>Nome fantasia</InputLabel>
                        <OutlinedInput fullWidth autoComplete="off" value={form.tradeName || ''} onChange={(e) => update({ tradeName: e.target.value })} />
                    </Grid>
                    <Grid item xs={12} sm={5}>
                        <InputLabel required>CNPJ</InputLabel>
                        <InputMask mask="99.999.999/9999-99" value={form.cnpj || ''} onChange={(e) => update({ cnpj: e.target.value })}>
                            <OutlinedInput fullWidth autoComplete="off" error={!!errors.cnpj} />
                        </InputMask>
                        <FieldError message={errors.cnpj} />
                    </Grid>
                    <Grid item xs={12}>
                        <InputLabel>Representante legal</InputLabel>
                        <OutlinedInput fullWidth autoComplete="off" value={form.legalRepresentativeName || ''} onChange={(e) => update({ legalRepresentativeName: e.target.value })} />
                    </Grid>
                </Grid>
            </FormDrawerGroup>

            <FormDrawerGroup title="Instrumento jurídico">
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <InputLabel required>Tipo de instrumento</InputLabel>
                        <Select fullWidth value={form.legalInstrumentType ?? ''} onChange={(e) => update({ legalInstrumentType: parseNumberInput(e.target.value) })} error={!!errors.legalInstrumentType}>
                            <MenuItem value=""><em>Selecionar</em></MenuItem>
                            {LEGAL_INSTRUMENT_TYPES.map((t) => <MenuItem key={t.id} value={t.id}>{t.label}</MenuItem>)}
                        </Select>
                        <FieldError message={errors.legalInstrumentType} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <InputLabel required>Nº do instrumento</InputLabel>
                        <OutlinedInput fullWidth autoComplete="off" value={form.instrumentNumber || ''} onChange={(e) => update({ instrumentNumber: e.target.value })} error={!!errors.instrumentNumber} />
                        <FieldError message={errors.instrumentNumber} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <InputLabel required>Valor transferido</InputLabel>
                        <BrazilianNumberInput
                            fullWidth
                            value={form.transferredAmount}
                            onChange={(transferredAmount) => update({ transferredAmount })}
                            decimals={2}
                            prefix="R$"
                            placeholder="0,00"
                            error={!!errors.transferredAmount}
                        />
                        <FieldError message={errors.transferredAmount} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <DateField label="Data de assinatura" required value={form.signatureDate} onChange={(value) => update({ signatureDate: value || undefined })} error={errors.signatureDate} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <DateField label="Início da vigência" required value={form.effectiveStartDate} onChange={(value) => update({ effectiveStartDate: value || undefined })} error={errors.effectiveStartDate} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <DateField label="Fim da vigência" required value={form.effectiveEndDate} onChange={(value) => update({ effectiveEndDate: value || undefined })} error={errors.effectiveEndDate} />
                    </Grid>
                </Grid>
            </FormDrawerGroup>
        </FormDrawer>
    );
}
